/**
 * Money — utilidad central para todos los importes monetarios.
 *
 * Regla dura: en TODO el codebase, un importe monetario se representa como bigint
 * en la unidad menor de la moneda (céntimos para EUR/USD, unidades para JPY, etc.).
 * NUNCA como float, NUNCA como number.
 *
 * Esta clase es inmutable. Todas las operaciones devuelven una nueva instancia.
 *
 * Rationale: ver docs/decisions/0003-bigint-cents-for-money.md
 */

import { getCurrency, type CurrencyCode } from './currencies';

export class Money {
  readonly cents: bigint;
  readonly currency: CurrencyCode;

  private constructor(cents: bigint, currency: CurrencyCode) {
    this.cents = cents;
    this.currency = currency;
  }

  /** Construye desde la unidad menor (céntimos). Uso primario en código de negocio. */
  static fromCents(cents: bigint | number, currency: CurrencyCode): Money {
    const value = typeof cents === 'bigint' ? cents : BigInt(Math.trunc(cents));
    return new Money(value, currency);
  }

  /**
   * Construye desde la unidad mayor (ej. 12.34 EUR).
   * Solo debe usarse en entrada de usuario o parsers explícitos.
   * Redondea half-even ("banker's rounding") para minimizar sesgo estadístico.
   */
  static fromMajor(amount: number, currency: CurrencyCode): Money {
    const { decimals } = getCurrency(currency);
    const multiplier = 10 ** decimals;
    // Round half-even
    const scaled = amount * multiplier;
    const rounded = halfEvenRound(scaled);
    return new Money(BigInt(rounded), currency);
  }

  static zero(currency: CurrencyCode): Money {
    return new Money(0n, currency);
  }

  /** Suma otro Money del mismo currency. Lanza si difieren. */
  add(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(this.cents + other.cents, this.currency);
  }

  subtract(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(this.cents - other.cents, this.currency);
  }

  /** Multiplica por un factor entero. Para porcentajes usa multiplyByRatio. */
  multiply(factor: bigint | number): Money {
    const f = typeof factor === 'bigint' ? factor : BigInt(Math.trunc(factor));
    return new Money(this.cents * f, this.currency);
  }

  /**
   * Multiplica por una razón (numerator/denominator) con redondeo half-even.
   * Uso típico: aplicar un porcentaje o una tasa de cambio.
   *
   * Delega en `roundHalfEven`, el mismo helper que usa `convertCents`. Tenía su
   * propia aritmética y sólo sabía ajustar hacia arriba (`q + 1`), lo que con
   * importes negativos —los refunds, que son de primera clase— redondeaba hacia
   * cero en vez de al par: −7/2 daba −3 donde el resto del dominio da −4.
   * Además el resto sale negativo con numerador negativo, así que la
   * comparación `r > halfMark` nunca se cumplía y el caso ni siquiera llegaba a
   * evaluarse.
   *
   * No afectaba a los saldos —`computeSplit` usa su propio `roundHalfEvenBigInt`
   * y nada más llamaba aquí—, pero dos redondeos distintos en el mismo módulo
   * de dinero son una trampa esperando a que alguien la pise.
   */
  multiplyByRatio(numerator: bigint, denominator: bigint): Money {
    if (denominator === 0n) throw new Error('Division by zero in multiplyByRatio');
    return new Money(roundHalfEven(this.cents * numerator, denominator), this.currency);
  }

  /** Divide en n partes iguales, devolviendo array. La suma exacta = this.cents. */
  divide(n: number): Money[] {
    if (n <= 0) throw new Error('divide requires n > 0');
    const nBig = BigInt(n);
    const base = this.cents / nBig;
    const remainder = this.cents - base * nBig;
    const result: Money[] = [];
    for (let i = 0; i < n; i++) {
      // Los primeros |remainder| reciben 1 extra (o -1 si el remainder es negativo)
      const extra = i < Number(remainder < 0n ? -remainder : remainder)
        ? (remainder < 0n ? -1n : 1n)
        : 0n;
      result.push(new Money(base + extra, this.currency));
    }
    return result;
  }

  negate(): Money {
    return new Money(-this.cents, this.currency);
  }

  abs(): Money {
    return new Money(this.cents < 0n ? -this.cents : this.cents, this.currency);
  }

  isZero(): boolean {
    return this.cents === 0n;
  }

  isPositive(): boolean {
    return this.cents > 0n;
  }

  isNegative(): boolean {
    return this.cents < 0n;
  }

  sign(): -1 | 0 | 1 {
    if (this.cents > 0n) return 1;
    if (this.cents < 0n) return -1;
    return 0;
  }

  equals(other: Money): boolean {
    return this.currency === other.currency && this.cents === other.cents;
  }

  compare(other: Money): -1 | 0 | 1 {
    this.assertSameCurrency(other);
    if (this.cents < other.cents) return -1;
    if (this.cents > other.cents) return 1;
    return 0;
  }

  /** Convierte a la unidad mayor como number. USO SOLO PARA DISPLAY. */
  toMajor(): number {
    const { decimals } = getCurrency(this.currency);
    const divisor = 10 ** decimals;
    return Number(this.cents) / divisor;
  }

  /** Formatea según locale del navegador o dado. */
  format(locale = 'es-ES'): string {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.currency,
    }).format(this.toMajor());
  }

  toString(): string {
    return `${this.cents.toString()} ${this.currency} (cents)`;
  }

  toJSON(): { cents: string; currency: CurrencyCode } {
    return { cents: this.cents.toString(), currency: this.currency };
  }

  private assertSameCurrency(other: Money): void {
    if (this.currency !== other.currency) {
      throw new Error(
        `Currency mismatch: cannot operate on ${this.currency} and ${other.currency}`
      );
    }
  }
}

/** Redondeo half-even (banker's rounding) para un número. */
function halfEvenRound(value: number): number {
  const rounded = Math.round(value);
  // Solo aplica half-even si estamos exactamente en el punto medio
  if (Math.abs(value - Math.floor(value) - 0.5) < Number.EPSILON) {
    const floor = Math.floor(value);
    return floor % 2 === 0 ? floor : floor + 1;
  }
  return rounded;
}

// ==================== Entrada de importes desde la UI ====================

/**
 * Normaliza el texto de un campo de importe MIENTRAS se teclea.
 *
 * Admite punto y coma como separador decimal (un teclado español produce coma)
 * y devuelve siempre punto. Conserva estados intermedios válidos como `"12."`
 * o `""` para que el usuario pueda seguir escribiendo: por eso los campos de
 * importe deben guardar ESTE texto como estado, no un número. Ligar el input a
 * un número reparsea en cada pulsación y borra el separador, lo que impedía
 * escribir decimales.
 */
export function normalizeAmountText(raw: string): string {
  const clean = raw.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
  const parts = clean.split('.');
  // Más de un separador: se conserva el primero y se pegan el resto de dígitos.
  return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : clean;
}

/**
 * Convierte el texto de un campo de importe a `Money` de la divisa indicada.
 * Devuelve `null` si no es un importe válido (vacío, incompleto o negativo).
 *
 * Usa `Money.fromMajor`, que aplica los decimales REALES de la divisa: hacer
 * `Math.round(x * 100)` a mano rompe en divisas sin decimales como el yen, y
 * `BigInt(NaN)` lanza `RangeError` con el campo vacío.
 */
export function parseAmountToMoney(text: string, currency: CurrencyCode): Money | null {
  const normalized = normalizeAmountText(text);
  if (normalized === '' || normalized === '.') return null;
  const n = Number(normalized);
  if (!Number.isFinite(n) || n < 0) return null;
  return Money.fromMajor(n, currency);
}

/**
 * Convierte un importe en la unidad mínima a `number` para enviarlo a la BD.
 *
 * Las columnas son `bigint`, pero PostgREST ya devuelve los enteros como número
 * JSON al leer (por eso los schemas aceptan `string | number`), así que enviar
 * texto al escribir no aportaba precisión de extremo a extremo. El rango exacto
 * de JS (2^53) equivale a ~9·10^13 unidades monetarias: de sobra para dinero.
 *
 * Si alguna vez se superara, es un bug y preferimos enterarnos en vez de
 * corromper el importe en silencio.
 */
export function centsToWire(cents: bigint): number {
  if (cents > BigInt(Number.MAX_SAFE_INTEGER) || cents < -BigInt(Number.MAX_SAFE_INTEGER)) {
    // Sin la cifra (#56): `centsToWire` se llama desde tres mutaciones —gasto,
    // recurrente y liquidación—, así que este texto acaba en
    // `client_errors.message`. Para depurarlo no hace falta el número: sólo se
    // llega aquí por encima de 2^53, y eso ya lo dice la propia frase.
    throw new Error('Importe fuera del rango exacto de JS');
  }
  return Number(cents);
}

/**
 * Convierte un importe entre divisas aplicando un tipo de cambio.
 *
 * El tipo de cambio se cotiza entre unidades MAYORES (1 EUR = 186,27 JPY),
 * mientras que los importes se guardan en unidades MENORES, y no todas las
 * divisas tienen los mismos decimales. Multiplicar los céntimos por el rate
 * directamente sólo funciona cuando origen y destino tienen los mismos: de
 * EUR (2) a JPY (0), 1000 céntimos (10 €) daban 186270 «céntimos» de yen, es
 * decir ¥186.270 en vez de ¥1.863. Cien veces de más.
 *
 * Redondeo half-even, igual que el resto del dominio.
 */
export function convertCents(
  cents: bigint,
  from: CurrencyCode,
  to: CurrencyCode,
  rate: number
): bigint {
  if (from === to) return cents;
  if (!Number.isFinite(rate) || rate <= 0) {
    throw new Error(`Tipo de cambio inválido ${from}->${to}: ${rate}`);
  }
  const fromDecimals = getCurrency(from).decimals;
  const toDecimals = getCurrency(to).decimals;

  // Se opera en enteros: el rate se lleva a una fracción con 10^12 de
  // denominador para no arrastrar el error binario de los flotantes.
  const SCALE = 1_000_000_000_000n;
  const rateScaled = BigInt(Math.round(rate * 1e12));
  const numerator = cents * rateScaled * 10n ** BigInt(toDecimals);
  const denominator = SCALE * 10n ** BigInt(fromDecimals);
  return roundHalfEven(numerator, denominator);
}

/** round(numerator / denominator) con redondeo al par en el punto medio. */
function roundHalfEven(numerator: bigint, denominator: bigint): bigint {
  const q = numerator / denominator;
  const r = numerator - q * denominator;
  const twoR = r < 0n ? -r * 2n : r * 2n;
  const absDen = denominator < 0n ? -denominator : denominator;
  const step = numerator < 0n ? -1n : 1n;
  if (twoR < absDen) return q;
  if (twoR > absDen) return q + step;
  return q % 2n === 0n ? q : q + step;
}
